import { EmptyState, FlexBox, Spinner } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import {
  Instrument,
  InstrumentDataGranularity,
  InstrumentPanel,
  InstrumentSettings,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import {
  categorizeInstruments,
  HeatmapInstrument,
  MetricInstrument,
  MetricInstrumentsGrid,
  PresenceInstrument,
} from 'lib-analytics-ui';
import { memo, useCallback, useMemo } from 'react';

import { panelGridLayoutConfig } from 'analytics/components/panels/utils/panelGridLayoutConfig';

import { ChartInstrumentRenderer } from './components/chartInstrumentRenderer/chartInstrumentRenderer';
import { TableInstrumentRenderer } from './components/tableInstrumentRenderer/tableInstrumentRenderer';
import styles from './instrumentRenderer.module.scss';

type InstrumentRendererProps = {
  /** The containing panel. */
  panel: InstrumentPanel;
  /** The list of instruments. */
  instruments: readonly Instrument[];
  /** Whether data is currently fetching. */
  isFetching: boolean;
  /** Whether data has failed to fetch. */
  isError: boolean;
  /** Called to refetch the instrument data. */
  onReadInstruments: ReadInstrumentsQueryFn;
  /** Called to set instrument-specific settings. */
  onSetInstrumentSettings?: (settings: InstrumentSettings) => void;
};

export const InstrumentRenderer = memo(function InstrumentRenderer({
  panel,
  instruments,
  isFetching = false,
  isError = false,
  onReadInstruments,
  onSetInstrumentSettings,
}: InstrumentRendererProps): JSX.Element {
  const handleChangeGranularity = useCallback(
    (granularity: InstrumentDataGranularity) => {
      onSetInstrumentSettings?.({ granularity });
    },
    [onSetInstrumentSettings],
  );

  const groups = useMemo(
    () => categorizeInstruments(instruments),
    [instruments],
  );

  const metricInstruments = useMemo((): JSX.Element | null => {
    const { metrics } = groups;
    if (!metrics.length) return null;

    return (
      <MetricInstrumentsGrid
        fluid={panel.layout.w === panelGridLayoutConfig.cols}
      >
        {metrics.map((instrument, index) => (
          <MetricInstrument
            key={`${panel.id}-${instrument.type}-${index}`}
            instrument={instrument}
          />
        ))}
      </MetricInstrumentsGrid>
    );
  }, [groups, panel.id, panel.layout.w]);

  const otherInstruments = useMemo((): JSX.Element | null => {
    const { chart, heatmap, table, presence } = groups;

    if (chart) {
      return (
        <ChartInstrumentRenderer
          instrument={chart}
          panel={panel}
          onChangeGranularity={handleChangeGranularity}
        />
      );
    }

    if (heatmap) return <HeatmapInstrument instrument={heatmap} />;
    if (table)
      return (
        <TableInstrumentRenderer
          instrument={table}
          panel={panel}
          onReadInstruments={onReadInstruments}
        />
      );
    if (presence) return <PresenceInstrument instrument={presence} />;

    return null;
  }, [groups, handleChangeGranularity, onReadInstruments, panel]);

  if (isError) {
    return (
      <FlexBox flex="1" justifyContent="center" alignItems="center">
        <EmptyState
          title="Failed to load data"
          description={
            <>
              <span>Something went wrong while fetching the data.</span>{' '}
              <a href="#" onClick={() => onReadInstruments()}>
                Please try again.
              </a>
            </>
          }
          icon={null}
        />
      </FlexBox>
    );
  }

  // The loader is displayed as an overlay when data already exists and is being refreshed.
  const loaderClass = classnames(styles.loader, {
    [styles.loaderOverlaid]: instruments.length && isFetching,
  });

  return (
    <>
      {isFetching && (
        <FlexBox
          className={loaderClass}
          flex="1"
          justifyContent="center"
          alignItems="center"
        >
          <Spinner />
        </FlexBox>
      )}

      {instruments.length > 0 && (
        <div className={styles.root}>
          {metricInstruments}
          {otherInstruments}
        </div>
      )}
    </>
  );
});
